import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, ChevronRight, AlertCircle, Clock, Wrench as WrenchIcon } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { isoLocal } from "@/lib/dateUtils";
import { formatSlotShort } from "@/lib/slot";
import PageHeader from "@/components/PageHeader";
import JobStatusBadge, { JobPriorityBadge } from "@/components/JobStatusBadge";
import { Card, CardContent } from "@/components/ui/card";

const TABS = [
  { key: "today", label: "Today" },
  { key: "upcoming", label: "Upcoming" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
];

export default function EngineerJobsList() {
  const [tab, setTab] = useState("today");
  const today = useMemo(() => isoLocal(new Date()), []);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ today: 0, upcoming: 0, in_progress: 0, completed: 0 });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get("/jobs", { params: { bucket: tab, today, limit: 100 } })
      .then(({ data }) => {
        if (!mounted) return;
        setItems(data.items || []);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [tab, today]);

  // Refresh counts for all buckets (parallel)
  useEffect(() => {
    let mounted = true;
    Promise.all(TABS.map((t) => api.get("/jobs", { params: { bucket: t.key, today, limit: 1 } })
      .then(({ data }) => [t.key, data.total ?? (data.items || []).length])
      .catch(() => [t.key, 0])))
      .then((pairs) => { if (mounted) setCounts(Object.fromEntries(pairs)); });
    return () => { mounted = false; };
  }, [today, tab]);

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-5xl mx-auto" data-testid="engineer-jobs-page">
      <PageHeader testId="engineer-jobs-header" title="My Jobs" subtitle={`Today · ${today}`} />

      <div className="rounded-lg bg-muted p-1 flex gap-1 overflow-x-auto" role="tablist" data-testid="engineer-jobs-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            data-testid={`engineer-tab-${t.key}`}
            className={`flex-1 min-h-11 min-w-[92px] px-3 text-sm rounded-md transition-colors ${tab === t.key ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
          >
            {t.label} <span className="text-[10px] font-bold tracking-wider ml-1">{counts[t.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground" data-testid="engineer-jobs-loading">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="rounded-md"><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="engineer-jobs-empty">
          No jobs in this bucket.
        </CardContent></Card>
      ) : (
        <ul className="space-y-3" data-testid="engineer-jobs-list">
          {items.map((j) => <li key={j.id}><JobCard job={j} today={today} /></li>)}
        </ul>
      )}
    </div>
  );
}

function JobCard({ job, today }) {
  const todaySlots = (job.scheduled_slots || []).filter((s) => s.date === today);
  const primarySlot = todaySlots[0] || (job.scheduled_slots || [])[0];
  return (
    <Link
      to={`/my/jobs/${job.id}`}
      data-testid={`engineer-job-card-${job.id}`}
      className="block group"
    >
      <Card className="rounded-md hover:border-primary/40 hover:shadow-sm transition-all">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs font-bold" data-testid={`engineer-job-num-${job.id}`}>{job.job_number}</span>
                <JobStatusBadge status={job.status} />
                <JobPriorityBadge priority={job.priority} />
                {job.actions_required && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 border border-amber-500/40 rounded px-1.5 py-0.5">
                    <AlertCircle className="h-3 w-3" /> Actions
                  </span>
                )}
              </div>
              <div className="text-base font-semibold mt-1.5 flex items-start gap-1.5">
                <Briefcase className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <span className="truncate">{job.title}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">{job.customer_name} · {job.site_name}</div>
              {primarySlot && (
                <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" /> {formatSlotShort(primarySlot)}
                  {job.scheduled_slots.length > 1 && (
                    <span className="text-[10px] font-bold uppercase tracking-wider">+{job.scheduled_slots.length - 1} more</span>
                  )}
                </div>
              )}
              {job.machine_ids?.length ? (
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  <WrenchIcon className="h-3.5 w-3.5" /> {job.machine_ids.length} machine{job.machine_ids.length > 1 ? "s" : ""}
                </div>
              ) : null}
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
